/* localStorage with the sharp edges removed.

   Three of them, all real:

   · Safari in private mode throws on `setItem` once the quota is hit, and it
     throws from a *write*, which means an unguarded call takes down whatever
     was rendering. Everything here is wrapped.

   · A value written by an older build can be any shape at all. `load` takes a
     fallback and returns it on any parse failure rather than handing a
     half-migrated object to a component that will read `.length` off it.

   · Storage is shared across tabs. `subscribe` exists so a sign-out in one tab
     does not leave another tab rendering a signed-in header. */

const NS = 'hexclean:v1:'

export function load<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(NS + key)
    if (raw === null) return fallback
    return JSON.parse(raw) as T
  } catch {
    return fallback
  }
}

export function save<T>(key: string, value: T): void {
  try {
    localStorage.setItem(NS + key, JSON.stringify(value))
  } catch {
    /* Quota or a disabled store. Losing a cached preference is not worth
       interrupting the user over; anything that must not be lost belongs on
       the server, not here. */
  }
}

export function remove(key: string): void {
  try {
    localStorage.removeItem(NS + key)
  } catch {
    /* see save() */
  }
}

/** Cross-tab notification for one key. Returns an unsubscribe. */
export function subscribe(key: string, onChange: () => void): () => void {
  const handler = (e: StorageEvent) => {
    if (e.key === null || e.key === NS + key) onChange()
  }
  window.addEventListener('storage', handler)
  return () => window.removeEventListener('storage', handler)
}
